import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/flownect-logo.png";
import { useStore, type AreaId } from "../state/store";
import { Icon } from "./Icon";

const EXISTING = "Este módulo ya existe en FlowNect. En la integración, este botón abre la pantalla actual sin cambios.";
const RAIL: { icon: string; label: string; area?: AreaId; route?: string; soon?: string }[] = [
  { icon: "home", label: "Inicio", area: "inicio" },
  { icon: "cal", label: "Agenda", area: "agenda" },
  { icon: "clip", label: "Trabajos", route: "/business/jobs" },
  { icon: "users", label: "Clientes", route: "/business/clients" },
  { icon: "doc", label: "Estimados", soon: EXISTING },
  { icon: "money", label: "Cobros", route: "/business/invoices" },
  { icon: "pay", label: "Nómina", soon: EXISTING },
  { icon: "phone", label: "Flow Mobile", soon: EXISTING },
];

export function Shell({ children }: { children: ReactNode }) {
  const s = useStore();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const activeRail = s.active === "inicio" ? "Inicio" : ["agenda", "espacio", "colores"].includes(s.active) ? "Agenda" : "";
  return (
    <div className="shell">
      <nav className={`rail${expanded ? " expanded" : ""}`} aria-label="Principal">
        <div className="rail-head">
          <img className="rail-logo" src={logo} alt="FlowNect" />
          <button
            type="button"
            className="rail-toggle"
            aria-label={expanded ? "Contraer menú" : "Abrir menú"}
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            <span aria-hidden="true">{expanded ? "‹" : "☰"}</span>
          </button>
        </div>
        {RAIL.map((r) => (
          <button
            key={r.label}
            type="button"
            title={r.label}
            aria-label={r.label}
            aria-current={activeRail === r.label ? "page" : undefined}
            className={`rail-item${activeRail === r.label ? " on" : ""}`}
            onClick={() => (r.area ? s.go(r.area) : r.route ? navigate(r.route) : s.showSoon({ title: r.label, body: r.soon ?? "" }))}
          >
            <Icon name={r.icon} size={20} />
            <span className="rail-label">{r.label}</span>
          </button>
        ))}
      </nav>
      <div className="main">
        <div className="agenda-toolbar">
          <div className="row" style={{ paddingBottom: 6 }}>
            <label className="search">
              <Icon name="search" color="#8A8B8E" size={16} />
              <span className="sr-only">Buscar</span>
              <input type="search" placeholder="Buscar cliente, teléfono, factura…" />
            </label>
            <button
              type="button"
              className="btn pri"
              style={{ minHeight: 40 }}
              onClick={() => {
                s.setCaller(null);
                s.go("espacio");
              }}
            >
              <Icon name="phone" size={16} />
              ¿Hay espacio?
            </button>
          </div>
        </div>
        {children}
        <div className="dock" aria-label="Áreas abiertas">
          <span className="dock-label">Abiertas</span>
          {s.open.map((a) => (
            <span key={a} className={`dock-item${s.active === a ? " on" : ""}`}>
              <button
                type="button"
                onClick={() => s.go(a)}
                style={{ background: "none", border: 0, font: "inherit", color: "inherit", cursor: "pointer", fontWeight: 700 }}
              >
                {s.areaLabel(a)}
              </button>
              {s.open.length > 1 && (
                <button type="button" className="dock-x" aria-label={`Cerrar ${s.areaLabel(a)}`} onClick={() => s.close(a)}>
                  <Icon name="close" size={14} />
                </button>
              )}
            </span>
          ))}
        </div>
      </div>
      {s.soon && <ComingSoon />}
      {s.toast && (
        <div className="toast" role="status">
          {s.toast}
        </div>
      )}
    </div>
  );
}

function ComingSoon() {
  const s = useStore();
  const info = s.soon!;
  return (
    <div className="backdrop" role="dialog" aria-modal="true" aria-labelledby="soon-title" onClick={() => s.showSoon(null)}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <span className="soon" style={{ alignSelf: "flex-start" }}>
          Próximamente
        </span>
        <h2 id="soon-title">{info.title}</h2>
        <p className="muted" style={{ margin: 0 }}>
          {info.body}
        </p>
        <button type="button" className="btn dark" onClick={() => s.showSoon(null)}>
          Entendido
        </button>
      </div>
    </div>
  );
}

/** Modal genérico de confirmación (para acciones simuladas). */
export function Confirm({
  title,
  children,
  onCancel,
  onConfirm,
  confirmLabel,
}: {
  title: string;
  children: ReactNode;
  onCancel(): void;
  onConfirm(): void;
  confirmLabel: string;
}) {
  return (
    <div className="backdrop" role="dialog" aria-modal="true" aria-labelledby="cf-title" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 id="cf-title">{title}</h2>
        {children}
        <div className="row wrap">
          <button type="button" className="btn pri" onClick={onConfirm}>
            {confirmLabel}
          </button>
          <button type="button" className="btn" onClick={onCancel}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
